import React, { useEffect } from 'react'
import {
  Button,
  useOverlay,
  DialogOverlay,
  DialogOverlayActions,
  Paragraph,
  DangerButton,
  DialogOverlayFooter,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

export default function ErrorOverlay({
  error,
  setError,
  title,
  message,
  label,
  callback,
  useDangerButton = false,
  onCancel = () => {},
}) {
  const overlay = useOverlay()
  const { isOpen, open, transitioning } = overlay
  const [t] = useTranslation()
  const defaultTitle = t('error-overlay.heading', 'Something went wrong on this page')
  const defaultMessage = t(
    'error-overlay.content',
    'An error occurred on this page. Please wait a few minutes and try again.'
  )
  const buttonLabel = label || t('error-overlay.submit', 'Refresh')

  const handleOnClick = () => {
    // Run the callback if it exists
    if (callback) {
      overlay.close() // Close the overlay first
      callback()
    } else {
      // Fallback to reload if no callback is provided as default
      window.location.reload()
    }
  }

  useEffect(() => {
    if (!isOpen) {
      if (transitioning) {
        setError(false)
      } else if (error) {
        open()
      }
    }
  }, [error, open, isOpen, transitioning, setError])

  return (
    <DialogOverlay
      {...{
        ...overlay,
        close: () => {
          overlay.close()
          onCancel()
        },
      }}
      title={title || defaultTitle}
    >
      <Paragraph size="sm">{message || defaultMessage}</Paragraph>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            data-testid="cancel-button"
            onClick={() => {
              overlay.close()
              onCancel()
            }}
          >
            {t('common-ui:cancel.action', 'Cancel')}
          </Button>
          {useDangerButton ? (
            <DangerButton data-testid="refresh-button" linkName="Error overlay dismiss" onClick={handleOnClick}>
              {buttonLabel}
            </DangerButton>
          ) : (
            <Button
              data-testid="refresh-button"
              linkName="Error overlay dismiss"
              onClick={handleOnClick}
              emphasis="high"
            >
              {buttonLabel}
            </Button>
          )}
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
